import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../auth/useAuth0';
import { useBusinessContext } from '../../../context/BusinessContext';
import { useAlert } from '../../../context/alert/AlertContext';
import LoadingComponent from '../../commons/LoadingComponent';
import Drawer from '../../commons/drawer/Drawer';
import ConfirmModal from '../../commons/confirm-modal/ConfirmModal';
import MoneyInput from '../../commons/money-input/MoneyInput';
import { currencyCode, formatMoney } from '../../../util/money';
import { useValueVisibility } from '../../../context/ValueVisibilityContext';
import { TeamMember } from '../../../model/teammember/TeamMember';
import { Salary, SalaryInput, SalaryPayment, SalaryType } from '../../../model/payment/Salary';
import {
    getSalaries,
    getSalaryPayments,
    saveSalary,
    deleteSalary,
    markSalaryPaid,
    unmarkSalaryPaid,
} from '../../../service/payment/salary-service';
import { getTeamMembersByBusinessId } from '../../../service/teammember/team-member-service';
import './SalariesTab.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ymd = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const currentYM = (): string => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
};

const SALARY_TYPE_KEYS: SalaryType[] = ['CLT', 'PJ', 'HOURLY'];

const EMPTY_INPUT = (businessId: string): SalaryInput => ({
    businessId,
    teamMemberId: null,
    teamMemberName: null,
    type: 'CLT',
    amount: 0,
    chargesPercent: 0,
    description: null,
    active: true,
    startDate: ymd(new Date()),
    endDate: null,
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SalariesTab: React.FC = () => {
    const { t } = useTranslation();
    const { getAccessTokenSilently } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const { setAlert } = useAlert();
    const { hidden } = useValueVisibility();

    const businessId = selectedBusiness?.id ? String(selectedBusiness.id) : '';
    const currency = currencyCode(selectedBusiness?.currency);

    // --- Month selector (drives salary-payment status) ---
    const [periodMonth, setPeriodMonth] = useState<string>(currentYM());

    // --- Data ---
    const [salaries, setSalaries] = useState<Salary[]>([]);
    const [payments, setPayments] = useState<SalaryPayment[]>([]);
    const [members, setMembers] = useState<TeamMember[]>([]);
    const [loading, setLoading] = useState(true);

    // --- Drawer ---
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [draft, setDraft] = useState<SalaryInput | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [amountStr, setAmountStr] = useState('');
    const [chargesStr, setChargesStr] = useState('');
    const [saving, setSaving] = useState(false);
    const [freeNameMode, setFreeNameMode] = useState(false);

    // --- Delete modal ---
    const [deleteTarget, setDeleteTarget] = useState<Salary | null>(null);
    const [deleteBusy, setDeleteBusy] = useState(false);

    // --- Mark/unmark busy per salaryId ---
    const [actionBusy, setActionBusy] = useState<string | null>(null);

    // --- Mark-all-paid ---
    const [markAllOpen, setMarkAllOpen] = useState(false);
    const [markAllBusy, setMarkAllBusy] = useState(false);

    // --- Load ---
    const reload = useCallback(async () => {
        if (!businessId) return;
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const [salaryList, paymentList, memberList] = await Promise.all([
                getSalaries(businessId, token),
                getSalaryPayments(businessId, periodMonth, token),
                getTeamMembersByBusinessId(businessId, token).catch(() => [] as TeamMember[]),
            ]);
            setSalaries(salaryList);
            setPayments(paymentList);
            setMembers(memberList);
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('payroll.salaries.alerts.loadError'));
        } finally {
            setLoading(false);
        }
    }, [businessId, periodMonth, getAccessTokenSilently, setAlert]);

    // Reload when period changes
    useEffect(() => { reload(); }, [reload]);

    // Reload payments-only when period changes (salaries list doesn't change)
    const reloadPayments = useCallback(async () => {
        if (!businessId) return;
        try {
            const token = await getAccessTokenSilently();
            const paymentList = await getSalaryPayments(businessId, periodMonth, token);
            setPayments(paymentList);
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('payroll.salaries.alerts.loadPaymentsError'));
        }
    }, [businessId, periodMonth, getAccessTokenSilently, setAlert]);

    // --- KPI derivations ---
    const activeSalaries = useMemo(() => salaries.filter((s) => s.active), [salaries]);
    const totalFolha = useMemo(() => activeSalaries.reduce((s, sal) => s + sal.monthlyCost, 0), [activeSalaries]);
    const totalPago = useMemo(() => payments.reduce((s, p) => s + p.amount, 0), [payments]);
    const totalPendente = totalFolha - totalPago;

    // --- Payment lookup ---
    const paymentBySalaryId = useMemo((): Map<string, SalaryPayment> => {
        const m = new Map<string, SalaryPayment>();
        payments.forEach((p) => m.set(p.salaryId, p));
        return m;
    }, [payments]);

    const pendingCount = useMemo(
        () => activeSalaries.filter((s) => !paymentBySalaryId.has(s.id)).length,
        [activeSalaries, paymentBySalaryId]
    );

    /** Members offered in the salary picker: only active ones (a deactivated
     *  member shouldn't get a new salary), plus the one already linked to the
     *  salary being edited so its value isn't silently dropped. Name resolution
     *  for existing rows still searches the full `members` list. */
    const selectableMembers = useMemo(() => {
        const active = members.filter((m) => m.active !== false);
        const id = draft?.teamMemberId ? String(draft.teamMemberId) : '';
        if (id && !active.some((m) => String(m.id) === id)) {
            const cur = members.find((m) => String(m.id) === id);
            if (cur) return [cur, ...active];
        }
        return active;
    }, [members, draft?.teamMemberId]);

    // --- Drawer helpers ---
    const openCreate = () => {
        setDraft(EMPTY_INPUT(businessId));
        setAmountStr('');
        setChargesStr('0');
        setEditingId(null);
        setFreeNameMode(false);
        setDrawerOpen(true);
    };

    const openEdit = (s: Salary) => {
        const hasMember = !!s.teamMemberId;
        setDraft({
            id: s.id,
            businessId,
            teamMemberId: s.teamMemberId ?? null,
            teamMemberName: s.teamMemberName ?? null,
            type: s.type,
            amount: s.amount,
            chargesPercent: s.chargesPercent,
            description: s.description ?? null,
            active: s.active,
            startDate: s.startDate,
            endDate: s.endDate ?? null,
        });
        setAmountStr(String(s.amount));
        setChargesStr(String(s.chargesPercent));
        setEditingId(s.id);
        setFreeNameMode(!hasMember);
        setDrawerOpen(true);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setDraft(null);
        setEditingId(null);
        setAmountStr('');
        setChargesStr('');
        setFreeNameMode(false);
    };

    const handleSave = async () => {
        if (!draft) return;
        const amount = parseFloat(amountStr);
        if (isNaN(amount) || amount <= 0) {
            setAlert('danger', t('payroll.salaries.alerts.invalidAmount'));
            return;
        }
        const charges = parseFloat(chargesStr) || 0;
        if (charges < 0 || charges > 200) {
            setAlert('danger', t('payroll.salaries.alerts.negativeCharges'));
            return;
        }
        if (!draft.startDate) {
            setAlert('danger', t('payroll.salaries.alerts.noStartDate'));
            return;
        }
        if (!draft.teamMemberName?.trim() && !draft.teamMemberId) {
            setAlert('danger', t('payroll.salaries.alerts.noMember'));
            return;
        }
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            await saveSalary({ ...draft, amount, chargesPercent: charges }, token);
            setAlert('success', editingId ? t('payroll.salaries.alerts.updated') : t('payroll.salaries.alerts.created'));
            closeDrawer();
            await reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('payroll.salaries.alerts.saveError'));
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            setDeleteBusy(true);
            const token = await getAccessTokenSilently();
            await deleteSalary(deleteTarget.id, businessId, token);
            setAlert('success', t('payroll.salaries.alerts.deleted'));
            setDeleteTarget(null);
            await reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('payroll.salaries.alerts.deleteError'));
        } finally {
            setDeleteBusy(false);
        }
    };

    const handleMarkPaid = async (salary: Salary) => {
        try {
            setActionBusy(salary.id);
            const token = await getAccessTokenSilently();
            await markSalaryPaid(salary.id, businessId, periodMonth, token);
            setAlert('success', t('payroll.salaries.alerts.markedPaid'));
            await reloadPayments();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('payroll.salaries.alerts.markError'));
        } finally {
            setActionBusy(null);
        }
    };

    const handleUnmark = async (salary: Salary) => {
        try {
            setActionBusy(salary.id);
            const token = await getAccessTokenSilently();
            await unmarkSalaryPaid(salary.id, businessId, periodMonth, token);
            setAlert('success', t('payroll.salaries.alerts.unmarked'));
            await reloadPayments();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('payroll.salaries.alerts.unmarkError'));
        } finally {
            setActionBusy(null);
        }
    };

    const handleMarkAllPaid = async () => {
        const pending = activeSalaries.filter((s) => !paymentBySalaryId.has(s.id));
        if (pending.length === 0) return;
        try {
            setMarkAllBusy(true);
            const token = await getAccessTokenSilently();
            await Promise.all(
                pending.map((s) => markSalaryPaid(s.id, businessId, periodMonth, token))
            );
            setAlert('success', t('payroll.salaries.alerts.markedAllPaid', { count: pending.length }));
            setMarkAllOpen(false);
            await reloadPayments();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('payroll.salaries.alerts.markAllError'));
        } finally {
            setMarkAllBusy(false);
        }
    };

    // --- Computed monthly cost hint ---
    const computedMonthlyCost = (() => {
        const amt = parseFloat(amountStr) || 0;
        const chg = parseFloat(chargesStr) || 0;
        return amt * (1 + chg / 100);
    })();

    // --- Member name helper ---
    const memberDisplayName = (s: Salary): string => {
        if (s.teamMemberName?.trim()) return s.teamMemberName.trim();
        if (s.teamMemberId) {
            const tm = members.find((m) => String(m.id) === String(s.teamMemberId));
            if (tm) return `${tm.firstName ?? ''} ${tm.lastName ?? ''}`.trim() || `#${s.teamMemberId}`;
            return `#${s.teamMemberId}`;
        }
        return s.description?.trim() || '—';
    };

    // fmt shorthand — respects value visibility toggle
    const fmt = (n: number) => hidden ? 'R$ ••••' : formatMoney(n, currency);

    // --- Month selector helpers ---
    const [selectorYear, selectorMonth] = periodMonth.split('-').map(Number);
    const prevMonth = () => {
        const d = new Date(selectorYear, selectorMonth - 2, 1);
        setPeriodMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    };
    const nextMonth = () => {
        const d = new Date(selectorYear, selectorMonth, 1);
        setPeriodMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    };
    const MONTH_KEYS = [
        'january', 'february', 'march', 'april', 'may', 'june',
        'july', 'august', 'september', 'october', 'november', 'december',
    ];

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------

    return (
        <div className="salaries-tab">

            {/* === Header: month selector + new button === */}
            <div className="salaries-header">
                <div className="salaries-month-nav">
                    <button
                        type="button"
                        className="salaries-month-nav__btn"
                        onClick={prevMonth}
                        aria-label={t('common.prevMonth')}
                    >
                        ‹
                    </button>
                    <span className="salaries-month-nav__label">
                        {t(`common.months.${MONTH_KEYS[selectorMonth - 1]}`)} {selectorYear}
                    </span>
                    <button
                        type="button"
                        className="salaries-month-nav__btn"
                        onClick={nextMonth}
                        aria-label={t('common.nextMonth')}
                    >
                        ›
                    </button>
                </div>

                <div className="salaries-header__actions">
                    <button
                        type="button"
                        className="btn btn-outline-success btn-sm"
                        onClick={() => setMarkAllOpen(true)}
                        disabled={pendingCount === 0 || loading}
                        title={pendingCount === 0 ? t('payroll.salaries.noPending') : t('payroll.salaries.markAllTitle', { count: pendingCount })}
                        data-cy="salaries-mark-all-paid-btn"
                    >
                        {t('payroll.salaries.markAll')}
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={openCreate}
                        data-cy="salaries-add-btn"
                    >
                        {t('payroll.salaries.addBtn')}
                    </button>
                </div>
            </div>

            {/* === KPI strip === */}
            <div className="salaries-kpi-strip">
                <div className="salaries-kpi" data-cy="salaries-kpi-total">
                    <span className="salaries-kpi__label">{t('payroll.salaries.kpi.total')}</span>
                    <span className="salaries-kpi__value" data-cy="salaries-kpi-total-value">{fmt(totalFolha)}</span>
                </div>
                <div className="salaries-kpi salaries-kpi--paid" data-cy="salaries-kpi-paid">
                    <span className="salaries-kpi__label">{t('payroll.salaries.kpi.paid')}</span>
                    <span className="salaries-kpi__value salaries-kpi__value--paid" data-cy="salaries-kpi-paid-value">{fmt(totalPago)}</span>
                </div>
                <div className="salaries-kpi salaries-kpi--pending" data-cy="salaries-kpi-pending">
                    <span className="salaries-kpi__label">{t('payroll.salaries.kpi.pending')}</span>
                    <span className="salaries-kpi__value salaries-kpi__value--pending" data-cy="salaries-kpi-pending-value">{fmt(Math.max(0, totalPendente))}</span>
                </div>
                <div className="salaries-kpi" data-cy="salaries-kpi-count">
                    <span className="salaries-kpi__label">{t('payroll.salaries.kpi.count')}</span>
                    <span className="salaries-kpi__value">{activeSalaries.length}</span>
                </div>
            </div>

            {/* === Salary table === */}
            {loading ? (
                <LoadingComponent />
            ) : salaries.length === 0 ? (
                <div className="salaries-empty">
                    <p>{t('payroll.salaries.empty')}</p>
                    <button type="button" className="btn btn-primary btn-sm" onClick={openCreate}>
                        {t('payroll.salaries.addFirst')}
                    </button>
                </div>
            ) : (
                <div className="list-card salaries-list-card">
                    <div className="payments-table-wrap">
                        <table className="payments-table salaries-table">
                            <thead>
                                <tr>
                                    <th>{t('payroll.salaries.table.member')}</th>
                                    <th>{t('payroll.salaries.table.type')}</th>
                                    <th className="payments-table__num">{t('payroll.salaries.table.base')}</th>
                                    <th className="payments-table__num">{t('payroll.salaries.table.monthlyCost')}</th>
                                    <th>{t('payroll.salaries.table.status')}</th>
                                    <th>{t('payroll.salaries.table.action')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Active salaries first */}
                                {salaries.filter((s) => s.active).map((s) => {
                                    const payment = paymentBySalaryId.get(s.id);
                                    const isPaid = !!payment;
                                    const isBusy = actionBusy === s.id;
                                    const displayName = memberDisplayName(s);
                                    const paidDate = payment?.paidAt
                                        ? new Date(payment.paidAt).toLocaleDateString('pt-BR')
                                        : null;
                                    return (
                                        <tr key={s.id} data-cy={`salaries-row-${s.id}`}>
                                            <td>
                                                <span
                                                    className="salaries-row__name"
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => openEdit(s)}
                                                    onKeyDown={(e) => e.key === 'Enter' && openEdit(s)}
                                                    title={t('payroll.salaries.editTitle')}
                                                >
                                                    {displayName}
                                                </span>
                                                {s.description && (
                                                    <div className="salaries-desc-chip">{s.description}</div>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`salaries-type-chip salaries-type-chip--${s.type.toLowerCase()}`}>
                                                    {t('payroll.salaries.type.' + s.type)}
                                                </span>
                                            </td>
                                            <td className="payments-table__num">
                                                {fmt(s.amount)}
                                                {s.chargesPercent > 0 && (
                                                    <span className="salaries-row__charges"> +{s.chargesPercent}%</span>
                                                )}
                                            </td>
                                            <td className="payments-table__num">
                                                <strong>{fmt(s.monthlyCost)}</strong>
                                            </td>
                                            <td>
                                                {isPaid ? (
                                                    <span className="salaries-status salaries-status--paid">
                                                        {t('payroll.commissions.status.paid', { date: paidDate })}
                                                    </span>
                                                ) : (
                                                    <span className="salaries-status salaries-status--pending">
                                                        {t('payroll.commissions.status.pending')}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                {isPaid ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-link text-secondary p-0"
                                                        disabled={isBusy}
                                                        onClick={() => handleUnmark(s)}
                                                        data-cy={`salaries-unmark-btn-${s.id}`}
                                                    >
                                                        {isBusy ? '…' : t('payroll.commissions.actions.undo')}
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-link p-0"
                                                        disabled={isBusy}
                                                        onClick={() => handleMarkPaid(s)}
                                                        data-cy={`salaries-mark-paid-btn-${s.id}`}
                                                    >
                                                        {isBusy ? '…' : t('payroll.commissions.actions.markPaid')}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {/* Inactive salaries — greyed at the bottom */}
                                {salaries.filter((s) => !s.active).map((s) => {
                                    const displayName = memberDisplayName(s);
                                    return (
                                        <tr key={s.id} className="salaries-table-row--inactive" data-cy={`salaries-row-${s.id}`}>
                                            <td>
                                                <span
                                                    className="salaries-row__name"
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => openEdit(s)}
                                                    onKeyDown={(e) => e.key === 'Enter' && openEdit(s)}
                                                    title={t('payroll.salaries.editTitle')}
                                                >
                                                    {displayName}
                                                </span>
                                                {s.description && (
                                                    <div className="salaries-desc-chip">{s.description}</div>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`salaries-type-chip salaries-type-chip--${s.type.toLowerCase()}`}>
                                                    {t('payroll.salaries.type.' + s.type)}
                                                </span>
                                                <span className="salaries-inactive-chip" style={{ marginLeft: '0.35rem' }}>{t('common.inactive')}</span>
                                            </td>
                                            <td className="payments-table__num">
                                                {fmt(s.amount)}
                                                {s.chargesPercent > 0 && (
                                                    <span className="salaries-row__charges"> +{s.chargesPercent}%</span>
                                                )}
                                            </td>
                                            <td className="payments-table__num">
                                                <strong>{fmt(s.monthlyCost)}</strong>
                                            </td>
                                            <td>
                                                <span className="salaries-status salaries-status--inactive">
                                                    {t('common.inactive')}
                                                </span>
                                            </td>
                                            <td />
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* === Drawer === */}
            <Drawer
                open={drawerOpen}
                onClose={closeDrawer}
                title={editingId ? t('payroll.salaries.drawer.editTitle') : t('payroll.salaries.drawer.newTitle')}
                subtitle={editingId ? t('payroll.salaries.drawer.editSubtitle') : t('payroll.salaries.drawer.newSubtitle')}
                size="md"
                footer={
                    <>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={saving}
                            data-cy="salaries-save-btn"
                        >
                            {saving ? t('common.working') : t('common.save')}
                        </button>
                        {editingId && (
                            <button
                                type="button"
                                className="btn btn-outline-danger"
                                disabled={saving}
                                onClick={() => {
                                    const target = salaries.find((s) => s.id === editingId);
                                    if (target) {
                                        setDeleteTarget(target);
                                        closeDrawer();
                                    }
                                }}
                                data-cy="salaries-delete-btn"
                            >
                                {t('common.delete')}
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={closeDrawer}
                            disabled={saving}
                        >
                            {t('common.cancel')}
                        </button>
                    </>
                }
            >
                {draft && (
                    <div className="finance-drawer-form" data-cy="salaries-drawer">

                        {/* Team member select or free name */}
                        <div className="form-group">
                            <label>{t('payroll.salaries.drawer.member')}</label>
                            {!freeNameMode ? (
                                <>
                                    <select
                                        className="gs-select"
                                        value={draft.teamMemberId ?? ''}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === '__free__') {
                                                setFreeNameMode(true);
                                                setDraft({ ...draft, teamMemberId: null, teamMemberName: '' });
                                            } else if (val) {
                                                const tm = members.find((m) => String(m.id) === val);
                                                const name = tm ? `${tm.firstName ?? ''} ${tm.lastName ?? ''}`.trim() : '';
                                                setDraft({ ...draft, teamMemberId: val, teamMemberName: name });
                                            } else {
                                                setDraft({ ...draft, teamMemberId: null, teamMemberName: null });
                                            }
                                        }}
                                    >
                                        <option value="">{t('payroll.salaries.drawer.selectPlaceholder')}</option>
                                        {selectableMembers.map((m) => (
                                            <option key={String(m.id)} value={String(m.id)}>
                                                {`${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() || `#${m.id}`}
                                            </option>
                                        ))}
                                        <option value="__free__">{t('payroll.salaries.drawer.otherName')}</option>
                                    </select>
                                </>
                            ) : (
                                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                                    <input
                                        type="text"
                                        className="gs-input"
                                        placeholder={t('payroll.salaries.drawer.namePlaceholder')}
                                        value={draft.teamMemberName ?? ''}
                                        onChange={(e) => setDraft({ ...draft, teamMemberName: e.target.value, teamMemberId: null })}
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => {
                                            setFreeNameMode(false);
                                            setDraft({ ...draft, teamMemberId: null, teamMemberName: null });
                                        }}
                                        title={t('payroll.salaries.drawer.useList')}
                                    >
                                        ↩
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Type chips */}
                        <div className="form-group">
                            <label>{t('payroll.salaries.drawer.typeLabel')}</label>
                            <div className="salaries-type-toggle" data-cy="salaries-type-select">
                                {SALARY_TYPE_KEYS.map((sk) => (
                                    <button
                                        key={sk}
                                        type="button"
                                        className={`salaries-type-btn ${draft.type === sk ? 'salaries-type-btn--active' : ''}`}
                                        onClick={() => setDraft({ ...draft, type: sk })}
                                        data-cy={`salaries-type-btn-${sk.toLowerCase()}`}
                                    >
                                        {t('payroll.salaries.type.' + sk)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Amount + charges */}
                        <div className="gs-form-grid">
                            <div className="form-group">
                                <label>
                                    {draft.type === 'HOURLY' ? t('payroll.salaries.drawer.hourlyRate') : t('payroll.salaries.drawer.baseAmount')}{' '}
                                    <span className="gs-required">*</span>
                                </label>
                                <MoneyInput
                                    currency={currency}
                                    value={amountStr}
                                    onChange={setAmountStr}
                                    placeholder="0.00"
                                />
                            </div>
                            <div className="form-group">
                                <label>{t('payroll.salaries.drawer.charges')}</label>
                                <input
                                    type="number"
                                    className="gs-input"
                                    min={0}
                                    max={200}
                                    step={0.01}
                                    placeholder="0"
                                    value={chargesStr}
                                    onChange={(e) => setChargesStr(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Monthly cost hint */}
                        {(parseFloat(amountStr) || 0) > 0 && (
                            <div className="salaries-drawer-hint">
                                {t('payroll.salaries.drawer.costHint')}{' '}
                                <strong>{fmt(computedMonthlyCost)}</strong>
                                {(parseFloat(chargesStr) || 0) > 0 && (
                                    <> ({amountStr} × (1 + {chargesStr || '0'}%))</>
                                )}
                            </div>
                        )}

                        {/* Description */}
                        <div className="form-group">
                            <label>{t('payroll.salaries.drawer.description')}</label>
                            <input
                                type="text"
                                className="gs-input"
                                placeholder={t('payroll.salaries.drawer.descriptionPlaceholder')}
                                value={draft.description ?? ''}
                                onChange={(e) => setDraft({ ...draft, description: e.target.value || null })}
                                data-cy="salaries-description-input"
                            />
                        </div>

                        {/* Dates */}
                        <div className="gs-form-grid">
                            <div className="form-group">
                                <label>{t('payroll.salaries.drawer.startDate')} <span className="gs-required">*</span></label>
                                <input
                                    type="date"
                                    className="gs-input"
                                    value={draft.startDate}
                                    onChange={(e) => setDraft({ ...draft, startDate: e.target.value || ymd(new Date()) })}
                                />
                            </div>
                            <div className="form-group">
                                <label>{t('payroll.salaries.drawer.endDate')}</label>
                                <input
                                    type="date"
                                    className="gs-input"
                                    value={draft.endDate ?? ''}
                                    min={draft.startDate}
                                    onChange={(e) => setDraft({ ...draft, endDate: e.target.value || null })}
                                />
                            </div>
                        </div>

                        {/* Active */}
                        <div className="form-group">
                            <label className="gs-inline-check" htmlFor="salary-active">
                                <input
                                    id="salary-active"
                                    type="checkbox"
                                    className="gs-inline-check__box"
                                    checked={draft.active}
                                    onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                                />
                                <span className="gs-inline-check__label">{t('payroll.salaries.drawer.active')}</span>
                            </label>
                        </div>
                    </div>
                )}
            </Drawer>

            {/* === Delete confirmation === */}
            <ConfirmModal
                open={!!deleteTarget}
                title={t('payroll.salaries.deleteModal.title')}
                danger
                busy={deleteBusy}
                confirmLabel={t('common.delete')}
                cancelLabel={t('common.cancel')}
                onConfirm={confirmDelete}
                onCancel={() => { if (!deleteBusy) setDeleteTarget(null); }}
                message={
                    deleteTarget && (
                        <>
                            {t('payroll.salaries.deleteModal.body', { name: memberDisplayName(deleteTarget) })}
                            <br />
                            {t('common.cannotUndo')}
                        </>
                    )
                }
            />

            {/* === Mark-all-paid confirmation === */}
            <ConfirmModal
                open={markAllOpen}
                title={t('payroll.salaries.markAllModal.title')}
                confirmLabel={t('common.confirm')}
                cancelLabel={t('common.cancel')}
                busy={markAllBusy}
                onConfirm={handleMarkAllPaid}
                onCancel={() => { if (!markAllBusy) setMarkAllOpen(false); }}
                message={
                    <>
                        {t('payroll.salaries.markAllModal.body', {
                            count: pendingCount,
                            month: t(`common.months.${MONTH_KEYS[selectorMonth - 1]}`),
                            year: selectorYear,
                        })}
                    </>
                }
            />
        </div>
    );
};

export default SalariesTab;
